import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { Folder20Regular } from '@fluentui/react-icons';
import React, { useState, useEffect } from 'react';
import {
  useFoldersQuery,
  useAssignRecordingToFolderMutation,
  useRemoveRecordingFromFolderMutation,
} from '../../queries/folders';

const useStyles = makeStyles({
  radioGroup: {
    width: '100%',
  },
  folderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxHeight: '300px',
    overflowY: 'auto',
    width: '100%',
  },
  radioItem: {
    width: '100%',
  },
  folderOption: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  folderIcon: {
    flexShrink: 0,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
});

interface FolderAssignDialogProps {
  recordingId: string | null;
  currentFolderId?: string;
  onClose: () => void;
}

export const FolderAssignDialog = ({
  recordingId,
  currentFolderId,
  onClose,
}: FolderAssignDialogProps) => {
  const styles = useStyles();
  const { data, isPending: isLoadingFolders } = useFoldersQuery();
  const folders = data?.folders;
  const assignMutation = useAssignRecordingToFolderMutation();
  const removeMutation = useRemoveRecordingFromFolderMutation();

  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(
    currentFolderId
  );

  // Reset selected folder when dialog opens with a new recording
  useEffect(() => {
    if (recordingId) {
      setSelectedFolderId(currentFolderId);
    }
  }, [recordingId, currentFolderId]);

  const handleSave = async () => {
    if (!recordingId) return;

    // If "none" is selected and there was a previous folder, remove from folder
    if (!selectedFolderId && currentFolderId) {
      await removeMutation.mutateAsync({
        folderId: currentFolderId,
        recordingId,
      });
    }
    // If a folder is selected (and it's different from current), assign to that folder
    else if (selectedFolderId && selectedFolderId !== currentFolderId) {
      await assignMutation.mutateAsync({
        folderId: selectedFolderId,
        recordingId,
      });
    }

    onClose();
  };

  const isLoading = assignMutation.isPending || removeMutation.isPending;

  return (
    <Dialog open={!!recordingId} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Assigner à un dossier</DialogTitle>
          <DialogContent>
            {isLoadingFolders ? (
              <div className={styles.loadingContainer}>
                <Spinner size="small" />
              </div>
            ) : (
              <RadioGroup
                className={styles.radioGroup}
                value={selectedFolderId || ''}
                onChange={(_, data) =>
                  setSelectedFolderId(data.value || undefined)
                }
              >
                <div className={styles.folderList}>
                  <Radio
                    className={styles.radioItem}
                    value=""
                    label={
                      <div className={styles.folderOption}>
                        <Folder20Regular className={styles.folderIcon} />
                        <Text>Aucun dossier (non classé)</Text>
                      </div>
                    }
                  />
                  {folders?.map((folder) => (
                    <Radio
                      className={styles.radioItem}
                      key={folder.id}
                      value={folder.id}
                      label={
                        <div className={styles.folderOption}>
                          <Folder20Regular
                            className={styles.folderIcon}
                            style={folder.color ? { color: folder.color } : undefined}
                          />
                          <Text>{folder.name}</Text>
                        </div>
                      }
                    />
                  ))}
                </div>
              </RadioGroup>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={isLoading}>
              Annuler
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={isLoading || isLoadingFolders}
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
